import { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { toast } from 'sonner';

interface ApplyServerValidationErrorsOptions<TFieldValues extends FieldValues> {
    /** Remap a backend field name to a form field path, e.g. 'room_number' -> `room_number.${index}` */
    fieldMap?: Record<string, Path<TFieldValues>>;
    /**
     * Form field names that have a visible invalid-feedback slot. Errors for fields NOT in
     * this list would be invisible if only attached via setError, so their message is
     * appended to the toast instead. Omit to skip this check (all fields treated as visible).
     */
    knownFields?: string[];
    /** Toast id to use so repeated submissions replace the same toast instead of stacking */
    toastId?: string;
    /** Message shown once field errors have been applied to the form */
    fieldErrorToastMessage?: string;
}

/**
 * Laravel returns 422s as { message: "Validation errors", errors: { field: string[] } } —
 * the top-level message is never useful on its own, the real detail is in `errors`.
 * Maps each backend field error onto the matching form field via `setError` so it renders
 * inline, and returns whether it handled the error (caller should still toast a short
 * "check the highlighted fields" message) or leave it to the caller's fallback toast.
 */
export function applyServerValidationErrors<TFieldValues extends FieldValues>(
    error: any,
    setError: UseFormSetError<TFieldValues>,
    options: ApplyServerValidationErrorsOptions<TFieldValues> = {}
): boolean {
    const { fieldMap, knownFields, toastId, fieldErrorToastMessage = 'There are errors in the form, please check and try again' } = options;

    const status = error?.response?.status ?? error?.status;
    const validationErrors = error?.response?.data?.errors;

    if (status !== 422 || !validationErrors || typeof validationErrors !== 'object') {
        return false;
    }

    const hiddenMessages: string[] = [];

    for (const [field, messages] of Object.entries(validationErrors)) {
        const fieldPath = (fieldMap?.[field] ?? field) as Path<TFieldValues>;
        const message = Array.isArray(messages) ? messages[0] : String(messages);
        setError(fieldPath, { type: 'manual', message });

        if (knownFields && !knownFields.includes(field) && !Object.prototype.hasOwnProperty.call(fieldMap ?? {}, field)) {
            hiddenMessages.push(message);
        }
    }

    toast.error(hiddenMessages.length > 0 ? hiddenMessages.join(' ') : fieldErrorToastMessage, { id: toastId });
    return true;
}

/** Merapikan payload error apa pun jadi satu string yang aman dirender. */
export function stringifyMessage(value: unknown): string | undefined {
    if (value == null) return undefined;
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
        const parts = value.map(stringifyMessage).filter(Boolean) as string[];
        return parts.length ? parts.join(' ') : undefined;
    }
    if (typeof value === 'object') {
        // Bentuk Laravel { field: [pesan] } -- juga dipakai API ini di key `message`.
        const parts = Object.values(value as Record<string, unknown>)
            .map(stringifyMessage).filter(Boolean) as string[];
        return parts.length ? parts.join(' ') : undefined;
    }
    return String(value);
}

/**
 * For call sites with no per-field error UI (no react-hook-form errors object to attach to):
 * pulls the specific backend validation message(s) out of a 422 response instead of falling
 * back to the generic top-level `message` (which is often just "Validation errors").
 *
 * `data.message` maupun `data.errors` bisa berupa object (bentuk Laravel { field: [msg] })
 * alih-alih string -- toast.error() akan meruntuhkan seluruh app kalau object itu diteruskan
 * mentah, jadi semuanya dirapikan lewat `stringifyMessage` sebelum sampai ke caller.
 */
export function extractErrorMessage(error: any, fallback?: string): string | undefined {
    return stringifyMessage(error?.response?.data?.errors)
        ?? stringifyMessage(error?.response?.data?.message)
        ?? stringifyMessage(error?.message)
        ?? fallback;
}
